/**
 * AI Analytics - Detecta y rastrea tráfico desde motores de AI
 * ChatGPT, Perplexity, Claude, Gemini, etc.
 *
 * Uso: importar y llamar a trackAIReferral() en el componente principal (App.js)
 */

// Lista de referrers de AI conocidos
const AI_REFERRERS = [
  { pattern: 'chatgpt.com', name: 'ChatGPT' },
  { pattern: 'chat.openai.com', name: 'ChatGPT' },
  { pattern: 'perplexity.ai', name: 'Perplexity' },
  { pattern: 'claude.ai', name: 'Claude' },
  { pattern: 'gemini.google.com', name: 'Gemini' },
  { pattern: 'copilot.microsoft.com', name: 'Copilot' },
  { pattern: 'poe.com', name: 'Poe' },
  { pattern: 'you.com', name: 'You.com' },
  { pattern: 'phind.com', name: 'Phind' },
  { pattern: 'writesonic.com', name: 'Writesonic' },
  { pattern: 'jasper.ai', name: 'Jasper' }
];

/**
 * Detecta si el referrer actual es de un motor de AI
 * @returns {object|null} Información del referrer AI o null
 */
export function detectAIReferrer() {
  const referrer = document.referrer;
  if (!referrer) return null;

  for (const ai of AI_REFERRERS) {
    if (referrer.includes(ai.pattern)) {
      return {
        source: ai.name,
        referrer: referrer,
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      };
    }
  }

  return null;
}

/**
 * Rastrea visita desde AI y la guarda en localStorage + envía a analytics
 */
export function trackAIReferral() {
  const aiRef = detectAIReferrer();

  if (aiRef) {
    // Guardar en localStorage para análisis posterior
    const aiVisits = JSON.parse(localStorage.getItem('geovis_ai_visits') || '[]');
    aiVisits.push(aiRef);
    localStorage.setItem('geovis_ai_visits', JSON.stringify(aiVisits.slice(-100))); // Últimas 100

    // Enviar evento a Google Analytics 4 (si está configurado)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'ai_referral', {
        ai_source: aiRef.source,
        referrer: aiRef.referrer,
        page_path: aiRef.page
      });
    }

    // Console log para desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log('[AI Analytics] Visita desde:', aiRef.source, aiRef);
    }

    return aiRef;
  }

  return null;
}

/**
 * Obtiene estadísticas de visitas desde AI
 * @returns {object} Estadísticas agregadas
 */
export function getAIVisitStats() {
  const visits = JSON.parse(localStorage.getItem('geovis_ai_visits') || '[]');

  const stats = {
    total: visits.length,
    bySource: {},
    byPage: {},
    recent: visits.slice(-10)
  };

  visits.forEach((visit) => {
    // Por fuente
    stats.bySource[visit.source] = (stats.bySource[visit.source] || 0) + 1;
    // Por página
    stats.byPage[visit.page] = (stats.byPage[visit.page] || 0) + 1;
  });

  return stats;
}

/**
 * Hook para usar en componentes React
 * Ejemplo: useAIReferral() en App.js
 */
export function useAIReferral() {
  if (typeof window !== 'undefined') {
    // Solo ejecutar una vez por sesión
    const sessionKey = 'geovis_ai_tracked_session';
    if (!sessionStorage.getItem(sessionKey)) {
      const result = trackAIReferral();
      sessionStorage.setItem(sessionKey, 'true');
      return result;
    }
  }
  return null;
}

/**
 * Regex para filtrar AI referrers en GA4
 * Copiar y usar en GA4 > Admin > Data Streams > Configure tag settings
 */
export const GA4_AI_REGEX =
  '.*chatgpt\\.com.*|.*perplexity\\.ai.*|.*gemini\\.google\\.com.*|.*copilot\\.microsoft\\.com.*|.*openai\\.com.*|.*claude\\.ai.*|.*poe\\.com.*|.*you\\.com.*|.*phind\\.com.*';

export default {
  detectAIReferrer,
  trackAIReferral,
  getAIVisitStats,
  useAIReferral,
  GA4_AI_REGEX
};
